"use client";

import { Button, SubmitButton } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { StepFooter, StepScrollArea } from "@/components/wizard";
import { pluralize } from "@/lib/string/pluralize";

import { useExportWizard } from "./context";
import {
  BalanceChange,
  CostBreakdown,
  CostPreview,
  NotEnoughCredits,
  ReviewSummary,
} from "./review";
import { ARTIFACT_FORMATS, NAME_MODES, exportThemeLabel } from "./types";

export function StepReview() {
  const {
    resolvedCount,
    formats,
    csvSelected,
    pdfSelected,
    theme,
    includeContacts,
    verifiedOnly,
    personalOnly,
    personalPreferred,
    hideExporter,
    nameMode,
    includeHighlights,
    csvColumns,
    creditsPerProfilePerFormat,
    reusedByFormat,
    creditsPerReveal,
    contactsToReveal,
    contactsAlreadyRevealed,
    contactCredits,
    totalCredits,
    estimateState,
    currentBalance,
    balanceAfter,
    hasEnoughCredits,
    setStep,
    handleConfirm,
  } = useExportWizard();

  return (
    <>
      <StepScrollArea contentClassName="gap-3">
        <p className="text-sm text-muted-foreground">
          Review your export before confirming.
        </p>

        <ReviewSummary>
          <ReviewSummary.Row
            label="Profiles"
            value={resolvedCount.toLocaleString()}
          />
          <ReviewSummary.Row
            label="Format"
            value={
              formats.length === 2
                ? "CSV + PDF"
                : formats[0] === "csv"
                  ? "CSV"
                  : "PDF"
            }
          />
          <ReviewSummary.Row
            label="Names"
            value={
              NAME_MODES.find((m) => m.value === nameMode)?.label ??
              "Show All Names"
            }
          />
          <ReviewSummary.Row
            label="Contact info"
            value={includeContacts ? "Included" : "Not included"}
          />
          {includeContacts && verifiedOnly && (
            <ReviewSummary.Row label="Contacts" value="Verified only" />
          )}
          {includeContacts && personalOnly && (
            <ReviewSummary.Row label="Emails" value="Personal only" />
          )}
          {includeContacts && personalPreferred && (
            <ReviewSummary.Row label="Emails" value="Personal preferred" />
          )}
          {!includeHighlights && (
            <ReviewSummary.Row label="AI highlights" value="Hidden" />
          )}
          {hideExporter && <ReviewSummary.Row label="Exporter" value="Hidden" />}
          {pdfSelected && (
            <ReviewSummary.Row
              label="PDF theme"
              value={exportThemeLabel(theme)}
            />
          )}
          {csvSelected && (
            <ReviewSummary.Row
              label="CSV columns"
              value={`${csvColumns.length} ${pluralize(csvColumns.length, "column")}`}
            />
          )}
        </ReviewSummary>

        <CostPreview
          state={estimateState}
          lines={formats.length + (includeContacts ? 1 : 0)}
        >
          <CostBreakdown>
            {/* Full price first, then the reuse discount as a real number, so
                every figure on screen reconciles against the profile count in
                the summary above. A row that silently pre-applied the discount
                would leave the reader deriving it. */}
            {ARTIFACT_FORMATS.filter((f) => formats.includes(f)).map((f) => {
              const credits = resolvedCount * creditsPerProfilePerFormat;
              return (
                <CostBreakdown.Line
                  key={f}
                  label={
                    <>
                      Export ({f.toUpperCase()})
                      {resolvedCount > 1 && (
                        <>
                          {" "}
                          &middot; {resolvedCount} &times;{" "}
                          {creditsPerProfilePerFormat}
                        </>
                      )}
                    </>
                  }
                >
                  {credits} {pluralize(credits, "credit")}
                </CostBreakdown.Line>
              );
            })}
            {/* One row per format: the counts differ per artifact, so a single
                summed row would read as a profile count and contradict the
                profile total above. */}
            {ARTIFACT_FORMATS.filter(
              (f) => formats.includes(f) && reusedByFormat[f] > 0,
            ).map((f) => {
              const saved = reusedByFormat[f] * creditsPerProfilePerFormat;
              return (
                <CostBreakdown.Line
                  key={f}
                  tone="saving"
                  label={
                    <>
                      Already exported ({f.toUpperCase()})
                      {resolvedCount > 1 && <> &middot; {reusedByFormat[f]}</>}
                    </>
                  }
                >
                  &minus;{saved} {pluralize(saved, "credit")}
                </CostBreakdown.Line>
              );
            })}
            {includeContacts && contactsToReveal > 0 && (
              <CostBreakdown.Line
                label={
                  <>
                    New contact reveals ({contactsToReveal} &times;{" "}
                    {creditsPerReveal})
                  </>
                }
              >
                {contactCredits} {pluralize(contactCredits, "credit")}
              </CostBreakdown.Line>
            )}
            {includeContacts && contactsAlreadyRevealed > 0 && (
              <CostBreakdown.Line
                tone="none"
                label={<>Already revealed ({contactsAlreadyRevealed})</>}
              >
                -
              </CostBreakdown.Line>
            )}
            <CostBreakdown.Total credits={totalCredits} plainWhenFree />
          </CostBreakdown>

          <BalanceChange before={currentBalance} after={balanceAfter} />

          {!hasEnoughCredits && (
            <NotEnoughCredits shortfall={totalCredits - currentBalance} />
          )}
        </CostPreview>
      </StepScrollArea>
      <StepFooter>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setStep(2)}
        >
          Back
        </Button>
        <SubmitButton
          type="button"
          size="sm"
          onClick={handleConfirm}
          disabled={
            estimateState === "loading" ||
            !hasEnoughCredits ||
            resolvedCount === 0
          }
          icon={<Icon name="IconArrowInbox" size={14} />}
          className="bg-[#FFAF53] border-[#e89a3a] text-black hover:bg-[#F5A347] active:bg-[#E8993A]"
        >
          Export
        </SubmitButton>
      </StepFooter>
    </>
  );
}
